import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Panel } from "primereact/panel";
import { formatDateTime } from "@/lib/utils";
import type { AdminEvent, AdminEventPricePoint } from "../types";

export function PricePointsPanel(props: {
  event: AdminEvent;
  formatPrice: (cents: number, currency: string) => string;
  onAdd: () => void;
  onMoveUp: (pricePointId: string) => void;
  onMoveDown: (pricePointId: string) => void;
  onEdit: (pricePoint: AdminEventPricePoint) => void;
  onRemove: (pricePointId: string) => void;
}) {
  const { event } = props;
  const sorted = [...event.pricePoints].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <Panel header="Price Points">
      <div className="relative">
        <div
          className="absolute top-0 right-0 z-10"
          style={{ marginTop: "-3.5rem", marginRight: "0.5rem" }}
        >
          <Button
            icon="pi pi-plus"
            label="Add Price Point"
            className="p-button-sm"
            onClick={props.onAdd}
          />
        </div>
        <DataTable value={sorted} emptyMessage="No price points" responsiveLayout="scroll">
          <Column
            field="label"
            header=""
            body={(rowData: AdminEventPricePoint) => (
              <div className="flex flex-column">
                <span className="font-bold">{rowData.label}</span>
                {rowData.description && <span className="mt-1">{rowData.description}</span>}
              </div>
            )}
          />
          <Column
            header="Price"
            body={(rowData: AdminEventPricePoint) =>
              props.formatPrice(rowData.priceCents, rowData.currency ?? "gbp")
            }
          />
          <Column
            header="Capacity"
            body={(rowData: AdminEventPricePoint) =>
              rowData.capacity != null ? rowData.capacity : "Unlimited"
            }
          />
          <Column
            header="Available From"
            body={(rowData: AdminEventPricePoint) =>
              rowData.availableFrom ? formatDateTime(rowData.availableFrom, event.timezone) : "-"
            }
          />
          <Column
            header="Available To"
            body={(rowData: AdminEventPricePoint) => {
              const availableTo = rowData.availableUntil || event.bookingClosesAt || event.startsAt;
              return availableTo ? formatDateTime(availableTo, event.timezone) : "-";
            }}
          />
          <Column
            header=""
            style={{ width: "120px", maxWidth: "120px", whiteSpace: "nowrap" }}
            body={(rowData: AdminEventPricePoint, options: { rowIndex: number }) => {
              const isFirst = options.rowIndex === 0;
              const isLast = options.rowIndex === sorted.length - 1;
              return (
                <div className="flex gap-1" style={{ whiteSpace: "nowrap", width: "100%" }}>
                  <Button
                    icon="pi pi-arrow-up"
                    className="p-button-text p-button-sm"
                    onClick={() => {
                      if (!rowData.id) return;
                      props.onMoveUp(rowData.id);
                    }}
                    aria-label="Move up"
                    style={{ visibility: isFirst ? "hidden" : "visible" }}
                  />
                  <Button
                    icon="pi pi-arrow-down"
                    className="p-button-text p-button-sm"
                    onClick={() => {
                      if (!rowData.id) return;
                      props.onMoveDown(rowData.id);
                    }}
                    aria-label="Move down"
                    style={{ visibility: isLast ? "hidden" : "visible" }}
                  />
                  <Button
                    icon="pi pi-pencil"
                    className="p-button-text p-button-sm"
                    onClick={() => props.onEdit(rowData)}
                    aria-label="Edit"
                  />
                  <Button
                    icon="pi pi-trash"
                    className="p-button-text p-button-sm p-button-danger"
                    onClick={() => {
                      if (!rowData.id) return;
                      props.onRemove(rowData.id);
                    }}
                    aria-label="Remove"
                    disabled={event.pricePoints.length === 1}
                  />
                </div>
              );
            }}
          />
        </DataTable>
      </div>
    </Panel>
  );
}


